import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  createTextWatermark,
  HistogramSeries,
  ISeriesApi,
  Time,
} from "lightweight-charts-v5";
import { fetchColor, formatEpochDateTime } from "@/helpers/helperFunctions";
import { AccentColor } from "@/helpers/types";
import { addGrid, addIndicatorToChart } from "./Indicators";
import { TickMarkType } from "lightweight-charts-v5";
import { TrendLine } from "./lightweight-charts/plugins/trend-line/trend-line";
import { isEmpty } from "lodash";
import {
  usePostCommonIndicators,
  usePostEarningsIndicator,
} from "@/api/strategyApi";
import { prepareChartData } from "@/lib/utils";

interface LightWeightChartProps {
  gridData?: any;
  graphData: any[];
  volumeData: any[];
  logsData?: any[];
  graphDataOtherDetails: any;
  canvasData: string;
  enableIndicator: boolean;
  currentPrice?: number;
  invertedChart?: boolean;
  monotone?: boolean;
}

const LightWeightChart: React.FC<LightWeightChartProps> = ({
  gridData,
  graphData,
  volumeData,
  logsData,
  graphDataOtherDetails,
  canvasData,
  enableIndicator,
  invertedChart,
  currentPrice,
  monotone,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const candleStickSeriesRef = useRef<any>(null);
  const cleanedGraphData = useMemo(
    () => prepareChartData(graphData),
    [graphData],
  );
  const chartRef = useRef<any>(null);
  const { mutateAsync: postCommonIndicators } = usePostCommonIndicators();
  const { mutateAsync: postEarningsIndicator } = usePostEarningsIndicator();
  const [earningsData, setEarningsData] = useState<any>(null);
  const [chartLoaded, setChartLoaded] = useState<boolean>(false);
  const prevSymbolsRef = useRef<string[]>([]);
  const indicatorSeriesRef = useRef<Record<string, ISeriesApi<"Line">[]>>({});
  const gridSeriesRef = useRef<any[]>([]);
  const volumeSeriesRef = useRef<any>(null);
  const logsSeriesRef = useRef<any[]>([]);
  const trendLinesRef = useRef<any[]>([]);

  const earningsSymbols = useMemo(() => {
    if (!canvasData) return [];
    try {
      const nodes = JSON.parse(canvasData);

      return Array.from(
        new Set(
          nodes
            .filter((n: any) => n?.data?.title === "Earnings")
            .map(
              (n: any) =>
                n?.data?.misc_data?.pair_symbol ?? graphDataOtherDetails.symbol,
            )
            .filter(Boolean),
        ),
      );
    } catch (e) {
      return [];
    }
  }, [canvasData, graphDataOtherDetails.symbol]);

  // 1. Initialize Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const colorMap = fetchColor({
      background: "transparent",
      foreground: "hsl(var(--foreground))",
      muted: "hsl(var(--muted))",
      mutedForeground: "hsl(var(--muted-foreground))",
      primary: "hsl(var(--accent-secondary))",
      transparent: "#ffffffff",
      accent: "hsl(var(--accent));",
    });

    const chart = createChart(chartContainerRef.current, {
      crosshair: { mode: 1 },
      layout: {
        background: { color: colorMap.background },
        textColor: colorMap.foreground,
        panes: {
          separatorColor: "#316acd",
          separatorHoverColor: "#316acd",
          enableResize: true,
        },
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      localization: {
        timeFormatter: (time: number) => {
          return formatEpochDateTime(time, "DD-MMM-YY HH:mm", true);
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        tickMarkFormatter: (
          time: Time,
          tickMarkType: TickMarkType,
          locale: string,
        ) => {
          if (tickMarkType === TickMarkType.Time) {
            return formatEpochDateTime(time as number, "HH:mm", true);
          } else if (tickMarkType === TickMarkType.DayOfMonth) {
            return formatEpochDateTime(time as number, "DD", true);
          } else if (tickMarkType === TickMarkType.Month) {
            return formatEpochDateTime(time as number, "MMM", true);
          }
          return null;
        },
      },
    });

    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect) {
        const { width, height } = entries[0].contentRect;
        chart.resize(width, height);
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    setChartLoaded(true);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      indicatorSeriesRef.current = {};
      gridSeriesRef.current = [];
      volumeSeriesRef.current = null;
      candleStickSeriesRef.current = null;
      logsSeriesRef.current = [];
      trendLinesRef.current = [];
      setChartLoaded(false);
    };
  }, [chartContainerRef]);

  // 2. Initialize and Update Main Series (Candlestick & Volume)
  useEffect(() => {
    if (!chartLoaded || !chartRef.current) return;

    if (!candleStickSeriesRef.current) {
      candleStickSeriesRef.current = chartRef.current.addSeries(
        CandlestickSeries,
        {
          wickUpColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
          upColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
          wickDownColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
          downColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
          borderVisible: false,
          priceFormat: { precision: 4, type: "price", minMove: 0.0001 },
        },
      );
    } else {
      candleStickSeriesRef.current.applyOptions({
        wickUpColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
        upColor: monotone ? AccentColor.DARKGREY : AccentColor.GREEN,
        wickDownColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
        downColor: monotone ? AccentColor.DARKGREY : AccentColor.RED,
      });
    }

    if (!volumeSeriesRef.current) {
      volumeSeriesRef.current = chartRef.current.addSeries(HistogramSeries, {
        color: "#26a69a",
        priceFormat: { type: "volume" },
        priceScaleId: "",
      });
      volumeSeriesRef.current.priceScale().applyOptions({
        scaleMargins: { top: 0.9, bottom: 0 },
      });
    }

    if (cleanedGraphData?.length) {
      candleStickSeriesRef.current.setData([...cleanedGraphData]);

      const visibleDataPoints = 1000;
      if (cleanedGraphData.length > visibleDataPoints) {
        const firstPoint =
          cleanedGraphData[cleanedGraphData.length - visibleDataPoints];
        const lastPoint = cleanedGraphData[cleanedGraphData.length - 1];
        chartRef.current.timeScale().setVisibleRange({
          from: firstPoint.time,
          to: lastPoint.time,
        });
      }
    }

    if (volumeData?.length) {
      volumeSeriesRef.current.setData(
        [...volumeData?.sort((a, b) => a.time - b.time)].filter(
          (item) => !isNaN(item.value),
        ),
      );
    }
  }, [chartLoaded, cleanedGraphData, volumeData, monotone]);

  // 3. Update Logs & Markers
  useEffect(() => {
    if (!chartLoaded || !chartRef.current || !candleStickSeriesRef.current)
      return;

    // Clean up previous log series and trend lines
    logsSeriesRef.current.forEach((s) => chartRef.current.removeSeries(s));
    logsSeriesRef.current = [];
    trendLinesRef.current.forEach((t) =>
      candleStickSeriesRef.current.detachPrimitive(t),
    );
    trendLinesRef.current = [];

    if (logsData?.length) {
      let markedData: any[] = [];
      const chart = chartRef.current;
      const lineSeries = candleStickSeriesRef.current;

      logsData.forEach((each, index) => {
        const baseColor = each?.result >= 0 ? "37, 146, 133" : "239, 68, 68";

        if (gridData ? index > logsData?.length - 120 : index < 120) {
          const tradeSeries = chart.addSeries(AreaSeries, {
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: false,
            lineColor: `rgba(${baseColor}, 1)`,
            topColor: `rgba(${baseColor}, 0.6)`,
            bottomColor: `rgba(${baseColor}, 0.05)`,
          });
          logsSeriesRef.current.push(tradeSeries);

          const openPrice =
            each?.filled_at_price ?? each?.order_price ?? each?.open_price;
          const closePrice =
            each?.close_value ??
            cleanedGraphData?.[cleanedGraphData.length - 1]?.open;
          const closedAt =
            each?.trade_closed_at ??
            cleanedGraphData?.[cleanedGraphData.length - 2]?.time;

          if (
            each?.trade_taken_at &&
            each?.order_status === "closed" &&
            openPrice &&
            closedAt
          ) {
            tradeSeries.setData([
              {
                time: Number(each?.trade_taken_at) as Time,
                value: Number(openPrice),
              },
              { time: Number(closedAt) as Time, value: Number(closePrice) },
            ]);
          }
        } else {
          const point1 = {
            time: Number(each?.trade_taken_at) as Time,
            price: each?.open_price ?? Number(each?.order_price),
          };
          const point2 = {
            time: Number(each?.trade_closed_at) as Time,
            price: each?.close_price ?? Number(each?.close_value),
          };
          const trend = new TrendLine(chart, lineSeries, point1, point2, {
            lineColor: `rgba(${baseColor}, 1)` || "red",
            width: 1.5,
            showLabels: false,
            labelBackgroundColor: "transparent",
            labelTextColor: "white",
          });
          lineSeries.attachPrimitive(trend);
          trendLinesRef.current.push(trend);
        }

        const baseMarker = {
          time: Number(each?.trade_taken_at),
          text: "O",
          position: each?.decision === "Sell" ? "aboveBar" : "belowBar",
          color: `rgba(${baseColor}, 1)`,
          shape: each?.decision === "Sell" ? "arrowDown" : "arrowUp",
        };
        markedData.push(baseMarker);

        if (each?.order_status === "closed") {
          const closedMarker = {
            time: Number(each?.trade_closed_at),
            text: "C",
            position: "aboveBar",
            color: `rgba(${baseColor}, 1)`,
            shape: "arrowDown",
            takenAt: each?.trade_taken_at,
          };
          markedData.push(closedMarker);
        }
      });

      markedData.sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
      );
      createSeriesMarkers(lineSeries, markedData);
    }
  }, [chartLoaded, logsData, cleanedGraphData, gridData]);

  // 4. Update Watermark
  useEffect(() => {
    if (!chartLoaded || !chartRef.current) return;

    createTextWatermark(chartRef.current.panes()[0], {
      horzAlign: "center",
      vertAlign: "center",
      lines: [
        {
          fontSize: 50,
          color: "rgba(229, 235, 241, 0.1 )",
          text: graphDataOtherDetails.symbol
            ? `${graphDataOtherDetails.symbol}, ${graphDataOtherDetails.timestamp}`
            : "No Data",
        },
      ],
    });
  }, [chartLoaded, graphDataOtherDetails]);

  useEffect(() => {
    if (earningsSymbols.length === 0) return;

    const prev = prevSymbolsRef.current;
    const isSame =
      prev.length === earningsSymbols.length &&
      prev.every((s, i) => s === earningsSymbols[i]);

    if (isSame) return; // 🚫 no real change → no API call

    prevSymbolsRef.current = earningsSymbols as string[];

    if (!graphDataOtherDetails.symbol) return;
    (async () => {
      const response = await postEarningsIndicator({
        ticker: earningsSymbols.join(","),
      });
      setEarningsData(response);
    })();
  }, [earningsSymbols, enableIndicator]);

  useEffect(() => {
    let ignore = false;

    if (!chartLoaded || !chartRef.current) return;

    const handler = setTimeout(() => {
      const updateIndicators = async () => {
        if (enableIndicator && canvasData) {
          try {
            const indicatorsData = JSON.parse(canvasData);

            // Clean up removed indicators
            const activeIds = indicatorsData
              .filter(
                (el: any) =>
                  el.type === "selectorNode" &&
                  (el?.data?.type === "Indicator" || el?.data?.title == "Grid"),
              )
              .map((el: any) => el.id);

            Object.entries(indicatorSeriesRef.current).forEach(
              ([key, seriesArray]) => {
                if (!activeIds.includes(key)) {
                  if (!seriesArray?.length) return;
                  seriesArray?.forEach((series: any) => {
                    try {
                      if (series.setData) {
                        chartRef.current?.removeSeries?.(series);
                      } else if (candleStickSeriesRef.current) {
                        candleStickSeriesRef.current?.removePriceLine?.(series);
                      }
                    } catch (e) {
                      console.warn("Failed to remove series:", e);
                    }
                  });
                  delete indicatorSeriesRef.current[key];
                }
              },
            );

            for (const element of indicatorsData) {
              if (ignore) return;

              const existingSeries = indicatorSeriesRef.current?.[element.id];
              if (
                element.type === "selectorNode" &&
                (element?.data?.type === "Indicator" ||
                  element?.data?.title == "Grid")
              ) {
                const lineSeries = await addIndicatorToChart({
                  indicator: {
                    ...element.data,
                    short_name:
                      element?.data?.title === "Grid"
                        ? "grid"
                        : element.data.short_name,
                    misc_data: {
                      pair_symbol:
                        element?.misc_data?.pair_symbol ??
                        graphDataOtherDetails.symbol,
                    },
                  },
                  chartRef,
                  tickerData: cleanedGraphData,
                  indicatorData: [],
                  lineSeriesRef:
                    existingSeries?.length > 0 ? existingSeries : undefined,
                  gridData: gridData,
                  candleStickSeriesRef: candleStickSeriesRef,
                  currentPrice: currentPrice,
                  postCommonIndicators: postCommonIndicators,
                  earningsData:
                    earningsData?.[
                      element?.misc_data?.pair_symbol ??
                        graphDataOtherDetails.symbol
                    ],
                });

                if (ignore) {
                  if (!existingSeries?.length && lineSeries) {
                    lineSeries.forEach((s: any) => {
                      try {
                        chartRef.current?.removeSeries(s);
                      } catch (error) {
                        console.warn(
                          "Failed to cleanup cancelled series",
                          error,
                        );
                      }
                    });
                  }
                  return;
                }

                if (!existingSeries?.length && lineSeries) {
                  indicatorSeriesRef.current[element.id] = [...lineSeries];
                }
              }
            }
          } catch (error) {
            console.error(
              "Error parsing canvasData or adding indicators:",
              error,
            );
          }
        }
      };

      updateIndicators();
    }, 10);

    return () => {
      ignore = true;
      clearTimeout(handler);
    };
  }, [
    chartLoaded,
    gridData,
    enableIndicator,
    earningsData,
    canvasData,
    cleanedGraphData,
    currentPrice,
  ]);

  useEffect(() => {
    if (!enableIndicator) {
      // Clean up indicator series
      if (chartRef.current) {
        Object.values(indicatorSeriesRef.current).forEach((seriesArray) => {
          seriesArray?.forEach((series: any) => {
            try {
              if (series.setData) {
                chartRef.current.removeSeries(series);
              } else if (candleStickSeriesRef.current) {
                candleStickSeriesRef.current.removePriceLine(series);
              }
            } catch (e) {
              console.warn("Failed to remove series:", e);
            }
          });
        });
        indicatorSeriesRef.current = {};
      }

      // Clean up grid series
      if (candleStickSeriesRef.current && gridSeriesRef.current?.length) {
        gridSeriesRef.current.forEach((line) => {
          try {
            candleStickSeriesRef.current.removePriceLine(line);
          } catch (e) {
            console.warn("Failed to remove price line:", e);
          }
        });
        gridSeriesRef.current = [];
      }
      chartRef.current.timeScale().applyOptions({
        visibleRange: undefined,
      });
    }
  }, [enableIndicator, chartLoaded]);

  useEffect(() => {
    if (chartLoaded && gridData && !isEmpty(gridData))
      if (enableIndicator && canvasData) {
        if (invertedChart) {
          chartRef.current.timeScale().setVisibleRange({
            from: cleanedGraphData[0]?.time,
            to: cleanedGraphData[100]?.time,
          }); // if it's a grid startegy set visible range to 100 points by default
        }
        const gridLines = addGrid(
          gridSeriesRef.current,
          { short_name: "grid" },
          chartRef,
          undefined,
          candleStickSeriesRef,
          gridData?.buy_levels,
          gridData?.sell_levels,
          gridData?.base_price,
        );
        if (gridLines) {
          gridSeriesRef.current = gridLines;
        }
      }
  }, [chartLoaded, gridData, enableIndicator]);

  return (
    <>
      <div
        ref={chartContainerRef}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      />
    </>
  );
};

export default memo(LightWeightChart);
